import React, { useEffect, useState } from 'react';
import { css } from 'emotion';
import { connect } from 'react-redux';
import { Page } from '../../../shared';
import { Input, Button } from '@weave/platform-ui-components';
import { DropdownField, FaxIconLarge, useFormField } from '@weave/design-system';
import { Typeahead } from 'react-bootstrap-typeahead';
import { countryOptions, CountryCodes } from '../../../../constants/country-codes';
import { stateOptions } from '../../../../constants/state-codes';
import { getAvailablePhoneNumbers } from '../../../../apis/phone-numbers/phone-numbers.api';
import { Provider } from '@weave/schema-gen-ts/dist/shared/porting/v1/enums.pb';
import { ListAvailableNumbersRequest } from 'apis/porting/porting.types';
import { orderNewNumberStyles } from '../order-new-number/order-new-number.styles';
import { AvailablePhoneNumber } from '@weave/schema-gen-ts/dist/schemas/phone/number-service/v2/number_service.pb';
import { AvailablePhoneNumbersTable } from '../order-new-number/available-phone-numbers';
import { Loader } from '../../../shared/loader/loader.component';
import { LocationModel, selectCurrentLocation } from '../../../../redux/actions/location';
import { Store } from '../../../../redux/store/store.model';
import { NumberToPurchase, purchaseNumbers } from '../purchase-number-utils';
import { useAlert } from '@weave/alert-system';

interface Props {
  currentLocation: LocationModel | null;
}

interface StateOption {
  name: string;
  abbreviation: string;
}

interface FaxNumberSearchFormValues {
  areaCode: string;
  city: string;
  state: string;
  zipCode: string;
  street: string;
  country: CountryCodes;
}

const initialFaxNumberSearchFormValues: FaxNumberSearchFormValues = {
  areaCode: '',
  city: '',
  state: '',
  zipCode: '',
  street: '',
  country: CountryCodes.USA,
};

interface PurchaseFaxNumberSearchFormProps {
  formValues: FaxNumberSearchFormValues;
  countryField: Omit<
    React.ComponentProps<typeof DropdownField>,
    'children' | 'name' | 'label'
  >;
  invalidState: boolean;
  isLoading: boolean;
  handleInputChange: (
    property: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  handleStateInputChange: (text: string) => void;
  handleStateBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  handleStateChange: (state: StateOption[]) => void;
  getStateOptions: () => StateOption[];
  findAvailablePhoneNumbers: (event: React.FormEvent) => Promise<void>;
}

const PurchaseFaxNumberSearchForm: React.FC<PurchaseFaxNumberSearchFormProps> = ({
  formValues,
  countryField,
  invalidState,
  isLoading,
  handleInputChange,
  handleStateInputChange,
  handleStateBlur,
  handleStateChange,
  getStateOptions,
  findAvailablePhoneNumbers,
}) => {
  return (
    <form
      className={css`
        display: grid;
        grid-template-columns: 100px repeat(auto-fit, minmax(100px, max-content));
        gap: 16px;
      `}
      autoComplete="off"
      onSubmit={findAvailablePhoneNumbers}
    >
      <Input
        type="text"
        placeholder="Area Code"
        value={formValues.areaCode}
        onChange={(e) => handleInputChange('areaCode', e)}
        required
      />
      <DropdownField {...countryField} name="country" label="Country">
        {countryOptions.map((country) => (
          <DropdownField.Option key={country.value} value={country.value}>
            {country.label}
          </DropdownField.Option>
        ))}
      </DropdownField>
      {formValues.country !== CountryCodes.USA && (
        <Typeahead
          id="fax-number-state-dropdown"
          className={orderNewNumberStyles.typeaheadStyles}
          isInvalid={invalidState}
          onInputChange={handleStateInputChange}
          onBlur={handleStateBlur}
          options={getStateOptions()}
          placeholder="Province"
          highlightOnlyResult
          labelKey="name"
          selected={(() => {
            try {
              const currentOptions = getStateOptions();
              const currentState = formValues.state;

              if (!currentState || !currentOptions) {
                return [];
              }

              const matchingOptions = currentOptions.filter(
                (option) => option && option.abbreviation === currentState
              );
              return matchingOptions || [];
            } catch (error) {
              console.error('Error filtering state options:', error);
              return [];
            }
          })()}
          onChange={handleStateChange}
        />
      )}
      <Button disabled={isLoading} type="submit">
        {isLoading ? 'Searching...' : 'Search'}
      </Button>
    </form>
  );
};

const PurchaseFaxNumber: React.FC<Props> = ({ currentLocation }) => {
  const [formValues, setFormValues] = useState<FaxNumberSearchFormValues>(
    initialFaxNumberSearchFormValues
  );
  const [invalidState, setInvalidState] = useState(false);
  const [selectedNumbers, setSelectedNumbers] = useState<string[]>([]);
  const [availableNumbers, setAvailableNumbers] = useState<{
    availablePhoneNumbers: AvailablePhoneNumber[];
    uri: string;
  }>({
    availablePhoneNumbers: [],
    uri: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const alert = useAlert();

  const formatPhoneNumbers = (phoneNumbers: string[]) => {
    const maxDisplayed = 10;
    if (phoneNumbers.length <= maxDisplayed) {
      return phoneNumbers.join(', ');
    }

    const displayed = phoneNumbers.slice(0, maxDisplayed).join(', ');
    return `${displayed} and ${phoneNumbers.length - maxDisplayed} more`;
  };

  const countryField = useFormField({
    type: 'dropdown',
    value: formValues.country || CountryCodes.USA,
  });

  const resetSearch = () => {
    setFormValues(initialFaxNumberSearchFormValues);
    setSelectedNumbers([]);
    setInvalidState(false);
    setAvailableNumbers({ availablePhoneNumbers: [], uri: '' });
    setIsLoading(false);
  };

  useEffect(() => {
    if (countryField.value !== formValues.country) {
      setFormValues((prev) => ({
        ...prev,
        country: countryField.value as CountryCodes,
        state: '',
      }));
      setInvalidState(false);
    }
  }, [countryField.value, formValues.country]);

  const handleInputChange = (
    property: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const val = event.target.value;
    setFormValues((prev) => ({
      ...prev,
      [property]: val,
    }));
  };

  const getStateOptions = (): StateOption[] => {
    const selectedCountry = formValues.country;
    if (selectedCountry === CountryCodes.Canada) {
      return stateOptions[CountryCodes.Canada].map((option) => ({
        name: option.label,
        abbreviation: option.value,
      }));
    }

    return stateOptions[CountryCodes.USA].map((option) => ({
      name: option.label,
      abbreviation: option.value,
    }));
  };

  const handleStateBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const val = event.target.value;
    const currentStateOptions = getStateOptions();
    const invalidStateResult = !currentStateOptions.find((state) => state.name === val);
    setInvalidState(invalidStateResult);
  };

  const handleStateInputChange = (text: string) => {
    if (invalidState) {
      const currentStateOptions = getStateOptions();
      const invalidStateResult = !currentStateOptions.find(
        (state) => state.name === text
      );
      setInvalidState(invalidStateResult);
    }
  };

  const handleStateChange = (state: StateOption[]) => {
    const stateAbbreviation = state[0]?.abbreviation;
    setFormValues((prev) => ({
      ...prev,
      state: stateAbbreviation,
    }));
    setInvalidState(false);
  };

  const findAvailablePhoneNumbers = async (event: React.FormEvent) => {
    event.preventDefault();
    setSelectedNumbers([]);
    setIsLoading(true);
    setAvailableNumbers({ availablePhoneNumbers: [], uri: '' });

    try {
      const request: ListAvailableNumbersRequest = {
        ...formValues,
        faxOnly: true,
      };
      const response = await getAvailablePhoneNumbers(request);

      const phoneNumbers = (response?.availablePhoneNumbers || []).map((phoneData) => ({
        phoneNumber: phoneData.phoneNumber || '',
        capabilities: phoneData.capabilities || {},
        provider:
          phoneData.provider === Provider.PROVIDER_UNSPECIFIED
            ? formValues.country === CountryCodes.USA
              ? Provider.PROVIDER_BANDWIDTH
              : Provider.PROVIDER_TELNYX
            : phoneData.provider,
      }));

      setAvailableNumbers({
        availablePhoneNumbers: phoneNumbers || [],
        uri: `${(phoneNumbers || []).length} numbers found`,
      });
      setIsLoading(false);
    } catch (error) {
      console.error('Search failed:', error);
      setAvailableNumbers({ availablePhoneNumbers: [], uri: 'Search failed' });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    resetSearch();
  }, [currentLocation?.LocationID]);

  const handlePurchaseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const purchaseEnabled = e.target.checked;
    const phoneNumber = e.target.value;
    const newSelectedNumbers = [...selectedNumbers];
    if (purchaseEnabled) {
      newSelectedNumbers.push(phoneNumber);
    } else {
      const index = newSelectedNumbers.indexOf(phoneNumber);
      newSelectedNumbers.splice(index, 1);
    }
    setSelectedNumbers(newSelectedNumbers);
  };

  const purchaseSelectedNumbers = () => {
    const numbersToPurchase: NumberToPurchase[] = selectedNumbers.map((phoneNumber) => {
      const phoneNumberData = availableNumbers.availablePhoneNumbers.find(
        (num) => num.phoneNumber === phoneNumber
      );

      return {
        phoneNumber,
        provider: phoneNumberData?.provider,
      };
    });

    const runPurchase = async () => {
      setIsPurchasing(true);

      try {
        const { purchasedNumbers, failures } = await purchaseNumbers({
          numbersToPurchase,
          faxOnly: true,
        });

        const purchasedPhoneNumbers = purchasedNumbers
          .map((result) => result.requestedPhoneNumber)
          .filter(Boolean);
        const failedPhoneNumbers = failures
          .map((failure) => failure.requestedPhoneNumber)
          .filter(Boolean);

        if (purchasedPhoneNumbers.length > 0) {
          alert.success(
            `Successfully purchased ${purchasedPhoneNumbers.length} fax number${
              purchasedPhoneNumbers.length === 1 ? '' : 's'
            }: ${formatPhoneNumbers(purchasedPhoneNumbers)}`
          );
        }

        if (failures.length > 0) {
          console.error('Fax number purchase failures', failures);
          alert.error(
            `Failed to purchase ${failedPhoneNumbers.length} fax number${
              failedPhoneNumbers.length === 1 ? '' : 's'
            }: ${formatPhoneNumbers(failedPhoneNumbers)}`
          );
        }
      } catch (error) {
        console.error('Unexpected fax purchase error', error);
        alert.error('Unable to complete fax number purchase. Please try again.');
      } finally {
        resetSearch();
        setIsPurchasing(false);
      }
    };

    runPurchase();
  };

  return (
    <Page
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Purchase Fax Number</span>
          <FaxIconLarge />
        </span>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <PurchaseFaxNumberSearchForm
          formValues={formValues}
          countryField={countryField}
          invalidState={invalidState}
          isLoading={isLoading}
          handleInputChange={handleInputChange}
          handleStateInputChange={handleStateInputChange}
          handleStateBlur={handleStateBlur}
          handleStateChange={handleStateChange}
          getStateOptions={getStateOptions}
          findAvailablePhoneNumbers={findAvailablePhoneNumbers}
        />

        <AvailablePhoneNumbersTable
          phoneNumbers={availableNumbers}
          subtitleText={availableNumbers.uri}
          selectedNumbers={selectedNumbers}
          handlePurchaseChange={handlePurchaseChange}
          loading={isLoading || isPurchasing}
          onPurchaseClick={purchaseSelectedNumbers}
          showPurchaseButton
        />

        {isLoading && (
          <div className={orderNewNumberStyles.loaderStyles}>
            <Loader size="large" />
          </div>
        )}
      </div>
    </Page>
  );
};

const mapStateToProps = (state: Store) => {
  return {
    currentLocation: selectCurrentLocation(state),
  };
};

export default connect(mapStateToProps)(PurchaseFaxNumber);
